import { useCallback, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";

/**
 * Backs onto private/auth's workerObservabilityRoutes.ts, which queries
 * Cloudflare's own persisted Workers Logs (Observability) directly —
 * there's no storage layer of ours to fall out of sync with. See that
 * file's own SCOPE NOTE for why events arrive here as raw, unshaped
 * JSON: the exact field names Cloudflare uses for this dataset aren't
 * confirmed against a real response yet, so this page makes no
 * assumptions about shape beyond "it's an object" and shows the raw JSON
 * per row alongside a best-effort extraction of the commonly-useful
 * fields (tried under several plausible key names each).
 */
interface WorkerLogEvent {
  [key: string]: unknown;
}

interface QueryResponse {
  ok: true;
  since: number;
  until: number;
  events: WorkerLogEvent[];
  /** Always the true, untouched `result` Cloudflare's API returned — see
   *  workerObservabilityRoutes.ts's own 2026-09-19 fix note: `events`
   *  above is only trustworthy once Array.isArray has actually confirmed
   *  it, since the real response shape was never live-verified against
   *  the docs it was built from. Shown here directly (rather than only in
   *  a devtools console someone might not have access to) so a shape
   *  mismatch is diagnosable from the page itself. */
  raw_result: unknown;
}

const WINDOW_OPTIONS = [
  { label: "Last hour", ms: 60 * 60 * 1000 },
  { label: "Last 6 hours", ms: 6 * 60 * 60 * 1000 },
  { label: "Last 24 hours", ms: 24 * 60 * 60 * 1000 },
  { label: "Last 3 days", ms: 3 * 24 * 60 * 60 * 1000 },
  { label: "Last 7 days", ms: 7 * 24 * 60 * 60 * 1000 },
];

function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

/** Tries several plausible dotted-key shapes for a field this dataset's
 *  exact schema hasn't been confirmed for yet — see this file's own
 *  header. Returns the first non-nullish match, or undefined. */
function pick(event: WorkerLogEvent, paths: string[]): unknown {
  for (const path of paths) {
    let cur: unknown = event;
    for (const seg of path.split(".")) {
      if (cur == null || typeof cur !== "object") {
        cur = undefined;
        break;
      }
      cur = (cur as Record<string, unknown>)[seg];
    }
    if (cur != null) return cur;
  }
  return undefined;
}

function fmtTime(v: unknown): string {
  const n =
    typeof v === "number" ? v : typeof v === "string" ? Date.parse(v) : NaN;
  if (!Number.isFinite(n)) return "—";
  return new Date(n).toLocaleString();
}

function outcomeVariant(
  outcome: unknown,
): "default" | "secondary" | "destructive" | "success" | "warning" {
  const s = String(outcome ?? "").toLowerCase();
  if (!s) return "secondary";
  if (s === "ok" || s === "success") return "success";
  if (s.includes("exception") || s.includes("error") || s === "canceled")
    return "destructive";
  return "warning";
}

export function WorkerLogsPage() {
  const token = useAuthToken();
  const call = useCallback(
    <T,>(path: string, init?: RequestInit) => api<T>(token, path, init),
    [token],
  );

  const [windowMs, setWindowMs] = useState(WINDOW_OPTIONS[2]!.ms);
  // Explicit since/until override — ADDED 2026-09-19, direct operator
  // request: the preset windows always anchor to "now", so once enough
  // traffic pushes a specific past moment beyond the 2000-row cap (a busy
  // box reconnecting every ~30s can do this in well under an hour), there
  // was NO way to reach back to it — "Last N hours" just returns the
  // newest 2000 rows in a wider net, not the specific older window
  // actually needed. `datetime-local` values (browser-local time,
  // matching how an operator actually thinks about "around 7pm") — empty
  // means "use the preset window" (unchanged existing behavior); setting
  // either one switches to this exact range instead.
  const [customSince, setCustomSince] = useState("");
  const [customUntil, setCustomUntil] = useState("");
  const [search, setSearch] = useState("");
  const [events, setEvents] = useState<WorkerLogEvent[] | null>(null);
  const [queriedRange, setQueriedRange] = useState<{
    since: number;
    until: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Set only when the response's `events` field ISN'T actually an array —
  // see load()'s own comment for the real 2026-09-19 incident this guards
  // against (a `TypeError: o?.map is not a function` crash, live, on a
  // real device, the moment this page's assumed response shape turned out
  // to be wrong). Holds the true raw Cloudflare response so the mismatch
  // is diagnosable from the page itself.
  const [shapeMismatch, setShapeMismatch] = useState<unknown>(undefined);
  // Which rows' raw-JSON <details> have actually been opened — the raw
  // event is only JSON.stringify'd (expensive, pretty-printed) for a row
  // once it's in this set, never eagerly for all loaded rows. See load()'s
  // own comment for the incident this fixes.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const load = useCallback(() => {
    setError(null);
    let until: number;
    let since: number;
    if (customSince || customUntil) {
      // Custom range — either field left blank falls back to "now" (until)
      // or "now minus the selected preset window" (since), so a half-set
      // custom range still does something sensible rather than silently
      // failing.
      until = customUntil ? new Date(customUntil).getTime() : Date.now();
      since = customSince ? new Date(customSince).getTime() : until - windowMs;
      if (!Number.isFinite(since) || !Number.isFinite(until)) {
        setError("Invalid custom date/time — check the since/until fields.");
        return;
      }
      if (since >= until) {
        setError("'Since' must be before 'until'.");
        return;
      }
    } else {
      until = Date.now();
      since = until - windowMs;
    }
    setLoading(true);
    // Back to the API's own max (2000) — 2026-09-19, revised same day:
    // this was dropped to the backend's 200 default right after the
    // eager-JSON.stringify-every-row crash (see the lazy-raw-JSON fix
    // below), but that fix was the wrong lever to also pull: with a busy
    // box reconnecting every ~30s for hours, a real 24h window can hold
    // thousands of `/ws` events alone, and 200 was silently truncating
    // results BEFORE the specific entries being searched for (rare,
    // targeted log lines like "jobs.subscribe: NOT_FOUND") ever reached
    // this page at all — indistinguishable from those entries genuinely
    // not existing. Now that rendering is actually lazy (this exact
    // commit) and search/sort are memoized, requesting the real max is
    // safe again — the render cost no longer scales with how many rows
    // came back, only with how many are actually expanded.
    const params = new URLSearchParams({
      since: String(since),
      until: String(until),
      limit: "2000",
    });
    call<QueryResponse>(`/admin/worker-logs?${params}`)
      .then((r) => {
        // Defensive, not just optional-chained: `r.events?.map` alone
        // crashed outright on a real device (2026-09-19) because `?.`
        // guards null/undefined, never a wrong TYPE — and the real
        // Cloudflare response shape turned out not to match what this
        // page was built against. Array.isArray actually proves it's
        // safe to treat as a list before anything downstream touches it.
        if (Array.isArray(r.events)) {
          setEvents(r.events);
          setShapeMismatch(undefined);
        } else {
          setEvents([]);
          setShapeMismatch(r.raw_result);
        }
        setQueriedRange({ since: r.since, until: r.until });
        setExpanded(new Set());
      })
      .catch((err) => setError(errMsg(err, "failed to query Workers Logs")))
      .finally(() => setLoading(false));
  }, [call, windowMs, customSince, customUntil]);

  // Precomputed ONCE per load (not per keystroke) — the other half of the
  // 2026-09-19 fix above: filtering used to call JSON.stringify(e) on
  // every event on every render (i.e. every keystroke while typing a
  // search), unmemoized. Harmless at a handful of rows, real, measurable
  // main-thread work at a couple hundred.
  const searchable = useMemo(
    () => events?.map((e) => JSON.stringify(e).toLowerCase()) ?? null,
    [events],
  );

  const filtered = useMemo(() => {
    if (!events || !searchable) return events;
    const needle = search.trim().toLowerCase();
    if (!needle) return events;
    return events.filter((_, i) => searchable[i]!.includes(needle));
  }, [events, searchable, search]);

  // Sorted once per (filtered, search) change, not on every render — same
  // "don't redo real work every keystroke" fix as `searchable` above.
  const sorted = useMemo(() => {
    if (!filtered) return [];
    return filtered.slice().sort((a, b) => {
      const ta =
        Number(
          pick(a, ["timestamp", "eventTimestamp", "$metadata.timestamp"]),
        ) || 0;
      const tb =
        Number(
          pick(b, ["timestamp", "eventTimestamp", "$metadata.timestamp"]),
        ) || 0;
      return tb - ta;
    });
  }, [filtered]);

  return (
    <div className="max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Worker Logs</h1>
        <p className="text-sm text-muted-foreground">
          Queries Cloudflare's own persisted Workers Logs (Observability) for
          this platform's Workers — retained 3-7 days depending on plan, not
          just live-tail. No box access or Cloudflare dashboard needed. Only
          Workers with{" "}
          <code className="font-mono">[observability] enabled = true</code> in
          their real deploy config (see deployConfigs.ts) are captured —
          currently just <code className="font-mono">pure-api</code>.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Query</CardTitle>
          <CardDescription>
            Results aren't live — click Search to (re)run the query for the
            selected window.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={String(windowMs)}
              onChange={(e) => setWindowMs(Number(e.target.value))}
              className="w-44"
              disabled={Boolean(customSince || customUntil)}
            >
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.ms} value={o.ms}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="filter loaded results (e.g. 500, ensureConnected, a job id)"
              className="w-96 font-mono text-xs"
            />
            <Button onClick={load} disabled={loading}>
              {loading ? "Searching…" : "Search"}
            </Button>
          </div>
          {/* Exact since/until range — ADDED 2026-09-19: the preset windows
              above always anchor to "now", so a specific past moment
              already pushed beyond the 2000-row cap by newer traffic was
              simply unreachable. Setting either field here switches the
              query to this exact range instead of the preset. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">
              or an exact range:
            </span>
            <Input
              type="datetime-local"
              value={customSince}
              onChange={(e) => setCustomSince(e.target.value)}
              className="w-56 text-xs"
              aria-label="since"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="datetime-local"
              value={customUntil}
              onChange={(e) => setCustomUntil(e.target.value)}
              className="w-56 text-xs"
              aria-label="until"
            />
            {(customSince || customUntil) && (
              <Button
                variant="secondary"
                onClick={() => {
                  setCustomSince("");
                  setCustomUntil("");
                }}
              >
                Clear
              </Button>
            )}
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {shapeMismatch !== undefined && (
            <div className="space-y-1">
              <p className="text-sm text-destructive">
                Cloudflare's response didn't have an{" "}
                <code className="font-mono">events</code> array where this page
                expected one — showing the raw response below instead of
                crashing. Whoever's fixing this next needs the real field names
                from here.
              </p>
              <pre className="whitespace-pre-wrap break-all bg-muted rounded p-2 text-[11px] overflow-x-auto max-h-64">
                {JSON.stringify(shapeMismatch, null, 2)}
              </pre>
            </div>
          )}
          {queriedRange && !error && (
            <p className="text-xs text-muted-foreground">
              Queried {new Date(queriedRange.since).toLocaleString()} –{" "}
              {new Date(queriedRange.until).toLocaleString()} —{" "}
              {events?.length ?? 0} event(s) returned
              {filtered && filtered.length !== events?.length
                ? `, ${filtered.length} matching filter`
                : ""}
              .
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Events</CardTitle>
          <CardDescription>
            Newest first. Expand a row for the full raw event — field extraction
            below is best-effort (see this page's own note above the table).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {events === null ? (
            <p className="text-sm text-muted-foreground">
              Pick a window and click Search.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((e, i) => {
                  const outcome = pick(e, ["outcome", "$workers.outcome"]);
                  const message = pick(e, [
                    "message",
                    "$metadata.message",
                    "$metadata.error",
                  ]);
                  const isOpen = expanded.has(i);
                  return (
                    <TableRow key={i} className="align-top">
                      <TableCell className="text-xs whitespace-nowrap">
                        {fmtTime(
                          pick(e, [
                            "timestamp",
                            "eventTimestamp",
                            "$metadata.timestamp",
                          ]),
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={outcomeVariant(outcome)}>
                          {outcome ? String(outcome) : "—"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs max-w-2xl">
                        <div className="truncate">
                          {message ? String(message) : "(see raw event)"}
                        </div>
                        {/* Lazy raw-JSON render — see load()'s own comment
                            for the incident this closes: only ever
                            JSON.stringify's THIS row's event, and only once
                            the operator actually opens it, never all rows
                            up front. */}
                        <details
                          className="mt-1"
                          open={isOpen}
                          onToggle={(ev) => {
                            const open = ev.currentTarget.open;
                            setExpanded((prev) => {
                              const next = new Set(prev);
                              if (open) next.add(i);
                              else next.delete(i);
                              return next;
                            });
                          }}
                        >
                          <summary className="cursor-pointer text-muted-foreground text-[11px]">
                            raw event
                          </summary>
                          {isOpen && (
                            <pre className="mt-1 whitespace-pre-wrap break-all bg-muted rounded p-2 text-[11px] overflow-x-auto">
                              {JSON.stringify(e, null, 2)}
                            </pre>
                          )}
                        </details>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {events && filtered?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No events match this filter in the queried window.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
